import "./navbar.scss";
import { AccountArea } from "@components/account";
import { NetworkStatusBar } from "@components/network-status-bar";
import { FC, memo, useMemo } from "react";
import { useTitle } from "@contexts/title.context";
import { BrandIcon, MenuIcon } from "@components/icons";
import { AWSUser } from "@archetypes/auth";
import { Button } from "@components/button";
import { useFavicon } from "@hooks/use-favicon";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { AppData } from "@archetypes/app";
import { AppMenuSchemaType } from "@dialogs/app-add-edit-dialog/app-add-edit-form.schema";
import { useIsMinimumTablet } from "@hooks/use-media-query";
import classNames from "classnames";
import { WorkSpaceEnum } from "@layouts/admin-layout";

interface NavbarProps {
  basePath: string;
  user?: AWSUser;
  appId?: string;
  onNavbarButtonClick?: () => void;
  appData?: AppData;
  menuData?: AppMenuSchemaType[];
  fontColor?: string;
  primaryColor?: string;
  navbarClassName?: string;
  onHamburgerClick?: () => void;
  tabsArray?: string[];
}

const NavbarComponent: FC<NavbarProps> = ({
  basePath,
  user,
  onNavbarButtonClick,
  appData,
  fontColor = "#000",
  navbarClassName,
  onHamburgerClick,
  tabsArray,
}) => {
  const { title } = useTitle();
  const { appId } = useParams();
  const { pathname } = useLocation();
  const isPublishedAppsView = pathname === "/apps";
  const isAppView = useMemo(() => pathname.startsWith(`/apps/${appId}`), [pathname, appId]);

  const baseRoute = useMemo(() => {
    return "/builder/apps/" + appId;
  }, [appId]);

  const appMetaData = useMemo(() => {
    if (isAppView && appData) {
      return {
        titlePrefix: appData.appName,
        appLogo: appData.appLogo,
        faviconLogo: appData.favicon,
      };
    }
    return undefined;
  }, [appData, isAppView]);

  const navbarTitle = useMemo(() => {
    if (!appMetaData?.titlePrefix) {
      return title;
    }
    if (title) {
      return appMetaData?.titlePrefix + " > " + title;
    }
    return appMetaData?.titlePrefix ?? "";
  }, [title, appMetaData]);

  useFavicon(appMetaData?.faviconLogo);

  const isMobile = !useIsMinimumTablet();

  return (
    <>
      <nav
        className={classNames("app-navbar", { "top-menu-container": tabsArray?.length })}
        aria-label="Main Navigation"
      >
        <div className="navbar-main">
          <div className={`flex align-center ${!tabsArray?.length ? "w-100-104" : ""}`}>
            {isAppView && isMobile && (
              <Button
                className={classNames("toggle-menu", navbarClassName)}
                onClick={() => onHamburgerClick?.()}
                variant="icon"
                style={{ background: fontColor }}
              >
                <MenuIcon />
              </Button>
            )}

            {isAppView && (
              <Button className="logo only-desktop" onClick={onNavbarButtonClick}>
                {appMetaData?.appLogo ? (
                  <img src={appMetaData.appLogo} alt="applogo" decoding="async" loading="eager" />
                ) : (
                  <BrandIcon size="30" color="#870160" />
                )}
              </Button>
            )}

            {isPublishedAppsView && (
              <Button className="logo only-desktop" onClick={onNavbarButtonClick}>
                <BrandIcon size="30" color="#870160" />
              </Button>
            )}

            <h1 className="page-title text-black">{navbarTitle}</h1>
          </div>

          {tabsArray && tabsArray?.length > 0 && (
            <ul className="top-menu">
              {tabsArray?.map((tab) => {
                const route = tab === WorkSpaceEnum.ManageForm ? "/forms" : "/dashboards";
                const isActive = pathname.endsWith(route);
                return (
                  <li key={tab} className={`text-black ${isActive ? "active" : ""}`}>
                    <NavLink to={`${baseRoute}${route}`}>{tab}</NavLink>
                  </li>
                );
              })}
            </ul>
          )}

          {user && (
            <div className="account-area">
              <AccountArea basePath={basePath} />
            </div>
          )}
        </div>
      </nav>
      <NetworkStatusBar />
    </>
  );
};

export const Navbar = memo(NavbarComponent);
